import _ from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import styles from './GroupSet.module.scss';
import setValuesStyles from './SetValues.module.scss';
import { Button } from '@mui/material';
import SetValues from './SetValues';
import {
  extractExerciseDragData,
  extractExerciseGroupDragData,
} from './exerciseDragUtils';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import OpenWithOutlinedIcon from '@mui/icons-material/OpenWithOutlined';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import DropZone from './DropZone';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { locales, DragTypes, indexToAlphabetMap } from '../../constants';
import { isExerciseAutomatic } from '../../utils';
import { useSessionData } from '../../providers';
import CameraIcon from '@mui/icons-material/CameraAlt';
import NoFotographyIcon from '@mui/icons-material/NoPhotography';
import AssetThumbnail from '../atoms/AssetThumbnail';

import Tooltip from '@mui/material/Tooltip';

import type {
  SetGroup,
  Exercise,
  TrainingSet,
  ExerciseGroup,
} from '../../types';
import type { AppState } from '../../index';

const exerciseGroupTexts = locales.en.components.workouts.exerciseGroup;

export interface GroupSetProps {
  exerciseGroup: ExerciseGroup;
  groupSetIndex: number;
  setGroup: SetGroup;
  updateSets: (sets: SetGroup) => void;
  exercises: AppState['exercises'];
  onSetGroupMoveToExistingExerciseGroup: (
    draggedExerciseGroupIndex: number,
    draggedSetGroupIndex: number,
    setGroup: SetGroup,
    targetSetIndex?: number,
  ) => void;
  onUnifyExerciseGroups: (
    draggedExerciseGroup: ExerciseGroup,
    draggedExerciseGroupIndex: number,
    targetExerciseGroupIndex: number,
    targetSetIndex?: number,
  ) => void;
  onExerciseDrop: (
    exercise: Exercise,
    exerciseGroupIndexToRemove?: number,
    groupIndexToRemove?: number,
  ) => void;
  exerciseGroupIndex: number;
  onReorderGroupSet: (draggedIndex: number, targetIndex: number) => void;
  enableInternalDrag: boolean;
  updateTrainerNote: (groupSetIndex: number, note: string) => void;
  totalGroupSets: number;
  onExerciseDropAtPosition: (
    exercise: Exercise,
    targetIndex: number,
    exerciseGroupIndexToRemove?: number,
    groupIndexToRemove?: number,
  ) => void;
  activeDragSource: { exerciseGroupIndex: number; groupSetIndex: number } | null;
  onGroupSetDragStart: (groupSetIndex: number) => void;
  onGroupSetDragEnd: () => void;
  onDropZoneEnter: () => void;
  onDropZoneLeave: () => void;
}

const setProperties = ['WEIGHT (LBS)', 'REPS', 'DURATION', 'REST', 'EFFORT'];
const metrics = ['CONCENTRIC'];

const GroupSet: React.FC<GroupSetProps> = ({
  exerciseGroup,
  setGroup,
  exercises,
  exerciseGroupIndex,
  groupSetIndex,
  onSetGroupMoveToExistingExerciseGroup,
  onUnifyExerciseGroups,
  onExerciseDrop,
  updateSets,
  onReorderGroupSet,
  enableInternalDrag,
  updateTrainerNote,
  totalGroupSets,
  onExerciseDropAtPosition,
  activeDragSource,
  onGroupSetDragStart,
  onGroupSetDragEnd,
  onDropZoneEnter,
  onDropZoneLeave,
}) => {
  const exercise = exercises[setGroup[0].exerciseId];
  if (!exercise) {
    throw new Error(
      `Exercises not found. exerciseId: ${setGroup[0].exerciseId}`,
    );
  }
  const { gym } = useSessionData();
  const [dropPosition, setDropPosition] = useState<'top' | 'bottom' | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const draggedSetGroupIndexRef = React.useRef<number | null>(null);
  const [cameraDisabled, setCameraDisabled] = useState<boolean>(() =>
    setGroup.some((trainingSet) => trainingSet.forcedManual === true),
  );

  const [renderKey, setRenderKey] = useState(0); // Add renderKey to force re-render on updates
  const isAutomatic = isExerciseAutomatic(exercise, gym?.id || '');
  const properties = isAutomatic
    ? setProperties.concat(metrics)
    : setProperties;

  // Custom horizontal scrollbar state and refs
  const setsRef = useRef<HTMLDivElement | null>(null);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(0);

  const recalcScrollbar = () => {
    const container = setsRef.current;
    if (!container) return;
    const { clientWidth, scrollWidth, scrollLeft } = container;
    const hasOverflow = scrollWidth > clientWidth + 1; // tolerance
    setShowScrollbar(hasOverflow);
    if (!hasOverflow) {
      setThumbLeft(0);
      setThumbWidth(0);
      return;
    }
    const trackWidth = clientWidth;
    const visibleRatio = clientWidth / scrollWidth;
    const computedThumbWidth = Math.max(
      24,
      Math.floor(trackWidth * visibleRatio),
    );
    const maxThumbLeft = trackWidth - computedThumbWidth;
    const maxScrollLeft = scrollWidth - clientWidth;
    const computedThumbLeft =
      maxScrollLeft > 0
        ? Math.round((scrollLeft / maxScrollLeft) * maxThumbLeft)
        : 0;
    setThumbWidth(computedThumbWidth);
    setThumbLeft(computedThumbLeft);
  };

  useEffect(() => {
    recalcScrollbar();
    const container = setsRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => recalcScrollbar());
    ro.observe(container);
    // Also observe first child (content grid) since its width affects scrollWidth
    if (container.firstElementChild instanceof HTMLElement) {
      ro.observe(container.firstElementChild);
    }
    const onWindowResize = () => recalcScrollbar();
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    recalcScrollbar();
  }, [setGroup.length, properties.length]);
  useEffect(() => {
    // Use an effect to force a re-render when setGroup change
    setRenderKey((prevKey) => prevKey + 1); // Change key to force re-render
  }, [setGroup.length]);

  // Sync sets' forcedManual flag with cameraDisabled state for automatic exercises
  useEffect(() => {
    if (!isAutomatic) return;

    if (cameraDisabled) {
      const needsUpdate = setGroup.some((set) => set.forcedManual !== true);
      if (needsUpdate) {
        const updated = setGroup.map((set) => ({ ...set, forcedManual: true }));
        updateSets(updated);
      }
    } else {
      const needsUpdate = setGroup.some((set) => set.forcedManual === true);
      if (needsUpdate) {
        const updated = setGroup.map((set) => {
          const { forcedManual, ...rest } = set;
          return rest as TrainingSet;
        });
        updateSets(updated);
      }
    }
  }, [cameraDisabled, setGroup, isAutomatic, updateSets]);

  const [noteValue, setNoteValue] = useState(
    exerciseGroup.trainerNotes?.[groupSetIndex] || '',
  );
  const noteRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNoteValue(exerciseGroup.trainerNotes?.[groupSetIndex] || '');
    // Auto-resize on external value change
    if (noteRef.current) {
      noteRef.current.style.height = 'auto';
      noteRef.current.style.height = noteRef.current.scrollHeight + 'px';
    }
  }, [exerciseGroup.trainerNotes, groupSetIndex]);

  // Compute the target insertion index based on drop position
  const getTargetIndex = (position: 'top' | 'bottom' | null): number => {
    return position === 'top' ? groupSetIndex : groupSetIndex + 1;
  };

  // Compute dynamic drop zone text based on context
  const getDropZoneText = (): string => {
    // Internal drag: source is from same exerciseGroup
    if (
      activeDragSource !== null &&
      activeDragSource.exerciseGroupIndex === exerciseGroupIndex
    ) {
      return exerciseGroupTexts.dropToAdjustPosition;
    }
    if (totalGroupSets === 1) {
      return exerciseGroupTexts.dropToCreateSuperset;
    }
    return exerciseGroupTexts.dropToAddToCircuit;
  };

  const [frozenDropZoneText, setFrozenDropZoneText] = useState(exerciseGroupTexts.dropToCreateSuperset);

  const handleDropInsideExerciseGroup = (e: React.DragEvent, position: 'top' | 'bottom' | null) => {
    e.preventDefault();
    e.stopPropagation();
    const data = e.dataTransfer.getData('application/json');
    const parsedData = JSON.parse(data);
    const draggedExerciseGroupIndex = parseInt(
      parsedData.exerciseGroupIndex,
      10,
    );
    const draggedSetGroupIndex = parseInt(parsedData.groupSetIndex, 10);
    const isSameExerciseGroup =
      draggedExerciseGroupIndex === exerciseGroupIndex;
    const targetIndex = getTargetIndex(position);

    switch (parsedData.type) {
      case DragTypes.GroupSetReorder:
        if (isSameExerciseGroup) {
          onReorderGroupSet(draggedSetGroupIndex, targetIndex);
        } else {
          const setGroup = parsedData.setGroup;
          onSetGroupMoveToExistingExerciseGroup(
            draggedExerciseGroupIndex,
            draggedSetGroupIndex,
            setGroup,
            targetIndex,
          );
        }
        break;
      case DragTypes.ExerciseGroupReorder:
        if (!isSameExerciseGroup) {
          const draggedExerciseGroup = extractExerciseGroupDragData(e);
          onUnifyExerciseGroups(
            draggedExerciseGroup,
            draggedExerciseGroupIndex,
            exerciseGroupIndex,
            targetIndex,
          );
        }
        break;
      case DragTypes.DragExercise:
        const exercise = extractExerciseDragData(e);
        onExerciseDropAtPosition(exercise, targetIndex);
        break;
      default:
        console.error(`unknown ${parsedData.type} drag type`);
        break;
    }
    draggedSetGroupIndexRef.current = null;
    setDropPosition(null);
    onDropZoneLeave();
    onGroupSetDragEnd();
    window.dispatchEvent(new CustomEvent('groupSetDropped'));
  };

  const groupSetRef = useRef<HTMLDivElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedSetGroupIndexRef.current === groupSetIndex) {
      return;
    }
    onDropZoneEnter();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedSetGroupIndexRef.current === groupSetIndex) {
      setDropPosition(null);
      return;
    }

    // Determine top/bottom based on cursor position
    const rect = groupSetRef.current?.getBoundingClientRect();
    let newPosition: 'top' | 'bottom' = 'bottom';
    if (rect) {
      const midY = rect.top + rect.height / 2;
      newPosition = e.clientY < midY ? 'top' : 'bottom';
    }

    if (dropPosition !== newPosition) {
      setDropPosition(newPosition);
    }
    if (dropPosition === null) {
      setFrozenDropZoneText(getDropZoneText());
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide if truly leaving the component (not entering a child)
    const relatedTarget = e.relatedTarget as Node | null;
    if (!groupSetRef.current?.contains(relatedTarget)) {
      setDropPosition(null);
      onDropZoneLeave();
    }
  };

  const addSet = () => {
    const newSet = _.last(setGroup);
    if (!newSet) {
      throw new Error('No sets defined.');
    }
    const normalizedNewSet: TrainingSet = (() => {
      if (isAutomatic && cameraDisabled) {
        return { ...newSet, forcedManual: true };
      }
      // Remove forcedManual when not disabled
      const { forcedManual, ...rest } = newSet;
      return rest as TrainingSet;
    })();

    const updatedSets = [...setGroup, normalizedNewSet];
    updateSets(updatedSets);
  };

  const updateSet = (updatedSet: TrainingSet, setIndex: number) => {
    // Normalize forcedManual according to cameraDisabled for automatic exercises
    let normalizedUpdatedSet: TrainingSet = updatedSet;
    if (isAutomatic && cameraDisabled) {
      normalizedUpdatedSet = { ...updatedSet, forcedManual: true };
    } else if (isAutomatic && !cameraDisabled && updatedSet.forcedManual) {
      const { forcedManual, ...rest } = updatedSet;
      normalizedUpdatedSet = rest as TrainingSet;
    }

    const updatedSets = setGroup.map((set, index) =>
      index === setIndex ? normalizedUpdatedSet : set,
    );
    updateSets(updatedSets);
  };

  const handleDeleteSet = (index: number) => {
    const updatedSets = setGroup.filter((_, i) => i !== index);
    updateSets([...updatedSets]);
  };

  const handleSetGroupDragStart = (e: React.DragEvent) => {
    const dragType = enableInternalDrag
      ? DragTypes.GroupSetReorder
      : DragTypes.ExerciseGroupReorder;
    const draggedItemData = {
      type: dragType,
      exerciseGroupIndex,
      groupSetIndex,
      exercise,
      setGroup,
      exerciseGroup,
    };
    e.dataTransfer.setData('application/json', JSON.stringify(draggedItemData));
    draggedSetGroupIndexRef.current = groupSetIndex;
    onGroupSetDragStart(groupSetIndex);
  };

  const handleDragEnd = () => {
    draggedSetGroupIndexRef.current = null;
    onGroupSetDragEnd();
  };

  // Handler for camera icon click (toggle)
  const handleCameraClick = () => {
    setCameraDisabled((prev) => !prev);
  };

  // Sync on scroll
  const handleSetsScroll = () => {
    recalcScrollbar();
  };

  // Dragging the custom thumb
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartThumbLeftRef = useRef(0);

  const onThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartThumbLeftRef.current = thumbLeft;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current || !setsRef.current) return;
      const container = setsRef.current;
      const { clientWidth, scrollWidth } = container;
      const trackWidth = clientWidth;
      const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
      const deltaX = ev.clientX - dragStartXRef.current;
      const newLeft = Math.min(
        Math.max(0, dragStartThumbLeftRef.current + deltaX),
        maxThumbLeft,
      );
      const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
      const newScrollLeft =
        maxThumbLeft > 0 ? (newLeft / maxThumbLeft) * maxScrollLeft : 0;
      container.scrollLeft = newScrollLeft;
      setThumbLeft(newLeft);
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // Clicking on the track to jump scroll position
  const onTrackMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!setsRef.current) return;
    // Ignore when clicking the thumb (its own handler will run)
    if ((e.target as HTMLElement).closest(`.${styles.customScrollbarThumb}`)) {
      return;
    }
    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const container = setsRef.current;
    const { clientWidth, scrollWidth } = container;
    const trackWidth = clientWidth;
    const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    const desiredLeft = Math.min(
      Math.max(0, clickX - thumbWidth / 2),
      maxThumbLeft,
    );
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    const newScrollLeft =
      maxThumbLeft > 0 ? (desiredLeft / maxThumbLeft) * maxScrollLeft : 0;
    container.scrollLeft = newScrollLeft;
    setThumbLeft(desiredLeft);
  };

  return (
    <div
      key={renderKey}
      className={styles.groupSet}
      ref={groupSetRef}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={(e) => {
        if (dropPosition) {
          handleDropInsideExerciseGroup(e, dropPosition);
        }
      }}
    >
      {/* Top drop zone */}
      <DropZone
        className={classNames(styles.dropZone, styles.dropZoneTop, {
          [styles.dropZoneVisible]: dropPosition === 'top',
        })}
        text={frozenDropZoneText}
      />
      <div
        className={classNames(styles.groupSetContent, {
          [styles.opened]: isOpen,
          [styles.circuit]: enableInternalDrag,
        })}
      >
        <AssetThumbnail
          className={styles.exerciseImage}
          src={exercise.imageUrl}
          context="exercise"
          alt={`${exercise.name} exercise`}
        />
        <div className={styles.groupDetails}>
          <div
            onClick={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
            className={classNames(styles.groupDetailsHeader, {
              [styles.withContent]: isOpen,
            })}
          >
            <div className={styles.groupDetailsTitle}>
              <span
                className={styles.exerciseNumber}
              >{`${indexToAlphabetMap[exerciseGroupIndex]}${groupSetIndex + 1}.`}</span>
              <div className={styles.groupDetailsIcon}>
                {isOpen ? (
                  <KeyboardArrowDownOutlinedIcon color="primary" />
                ) : (
                  <KeyboardArrowUpOutlinedIcon color="primary" />
                )}
              </div>
              <span className={styles.exerciseName}>{exercise.name}</span>
              <div
                draggable
                onDragStart={handleSetGroupDragStart}
                onDragEnd={handleDragEnd}
                className={styles.dragGroupSet}
              >
                <OpenWithOutlinedIcon
                  style={{ color: 'var(--color-text-3)' }}
                />
              </div>
            </div>
          </div>
          {isOpen && (
            <>
              <div className={styles.sets}>
                <div
                  className={styles.setsScroll}
                  ref={setsRef}
                  onScroll={handleSetsScroll}
                >
                  <div
                    className={classNames(
                      isAutomatic
                        ? setValuesStyles.automaticSetProperties
                        : setValuesStyles.setProperties,
                    )}
                  >
                    <span className={styles.keepSpanForEmptyEl} />
                    {properties.map((property) => {
                      if (property === 'CONCENTRIC') {
                        return (
                          <span className={styles.setProperty} key={property}>
                            {property}
                            {cameraDisabled ? (
                              <Tooltip
                                title="Automatic Analysis Disabled"
                                arrow
                                placement="top"
                                slotProps={{
                                  popper: {
                                    sx: {
                                      '& .MuiTooltip-tooltip': {
                                        backgroundColor: 'var(--color-bg-2)',
                                        color: 'var(--color-text-1)',
                                        fontWeight: 500,
                                        fontSize: '13px',
                                      },
                                    },
                                  },
                                }}
                              >
                                <span>
                                  <NoFotographyIcon
                                    style={{
                                      marginLeft: 1,
                                      cursor: 'not-allowed',
                                      color: 'var(--color-text-4)',
                                      width: '12px',
                                      height: '12px',
                                    }}
                                    fontSize="small"
                                    onClick={handleCameraClick}
                                  />
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title="Automatic Analysis Enabled"
                                arrow
                                placement="top"
                                slotProps={{
                                  popper: {
                                    sx: {
                                      '& .MuiTooltip-tooltip': {
                                        backgroundColor: 'var(--color-bg-2)',
                                        color: 'var(--color-text-1)',
                                        fontWeight: 500,
                                        fontSize: '13px',
                                      },
                                    },
                                  },
                                }}
                              >
                                <span>
                                  <CameraIcon
                                    style={{
                                      marginLeft: 1,
                                      cursor: 'pointer',
                                      color: 'var(--color-text-4)',
                                      width: '12px',
                                      height: '12px',
                                    }}
                                    fontSize="small"
                                    onClick={handleCameraClick}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </span>
                        );
                      }
                      return (
                        <span className={styles.setProperty} key={property}>
                          {property}
                        </span>
                      );
                    })}
                    <span className={styles.keepSpanForEmptyEl} />
                  </div>
                  {setGroup.map((set, index) => (
                    <SetValues
                      index={index}
                      set={set}
                      updateSet={(newSet) => updateSet(newSet, index)}
                      key={`${groupSetIndex}-setValues-${index}-${set.exerciseId}`}
                      deleteSet={() => handleDeleteSet(index)}
                      exercise={exercise}
                    />
                  ))}
                </div>
                {/* Custom horizontal scrollbar */}
                <div
                  className={classNames(styles.customScrollbar, {
                    [styles.visible]: showScrollbar,
                  })}
                >
                  <div
                    className={styles.customScrollbarTrack}
                    onMouseDown={onTrackMouseDown}
                  >
                    <div
                      className={styles.customScrollbarThumb}
                      style={{
                        width: `${thumbWidth}px`,
                        left: `${thumbLeft}px`,
                      }}
                      onMouseDown={onThumbMouseDown}
                    />
                  </div>
                </div>
              </div>
              <div onClick={addSet} className={styles.addSet}>
                <Button className={styles.addSetButton}>
                  <AddBoxOutlinedIcon />
                  Add Set
                </Button>
              </div>
              {/* Trainer Note Input */}
              <div className={styles.trainerNoteWrapper}>
                <textarea
                  className={styles.trainerNoteInput}
                  value={noteValue}
                  ref={noteRef}
                  onChange={(e) => {
                    setNoteValue(e.target.value);
                    updateTrainerNote(groupSetIndex, e.target.value);
                    // Auto-resize on input
                    if (noteRef.current) {
                      noteRef.current.style.height = 'auto';
                      noteRef.current.style.height =
                        noteRef.current.scrollHeight + 'px';
                    }
                  }}
                  placeholder="Add trainer note..."
                  style={{ resize: 'none', overflow: 'hidden' }}
                />
                <span className={styles.trainerNoteIcon}>
                  <EditNoteOutlinedIcon fontSize="medium" />
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Bottom drop zone */}
      <DropZone
        className={classNames(styles.dropZone, styles.dropZoneBottom, {
          [styles.dropZoneVisible]: dropPosition === 'bottom',
        })}
        text={frozenDropZoneText}
      />
    </div>
  );
};

export default GroupSet;
